<svelte:options
  customElement={{
    tag: "oc-icon-button-v3",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      backgroundColor: { type: "String", attribute: "background-color" },
      base64Href: { type: "String", attribute: "base64-href" },
      disabled: { type: "Boolean", reflect: true },
      elevationLevel: { type: "String", attribute: "elevation-level" },
      icon: { type: "String" },
      iconColor: { type: "String", attribute: "icon-color" },
      label: { type: "String" },
      loading: { type: "Boolean" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      rel: { type: "String" },
      size: { type: "String" },
      success: { type: "Boolean", reflect: true },
      target: { type: "String" },
      variant: { type: "String" },
    },
  }}
/>

<script lang="ts">
  import { fade } from "svelte/transition";
  import type { OcSpinnerV1Props } from "src/components/spinner";
  import type { OcIconV1Props } from "src/components/icon/v1/IconV1.types.g";
  import { InteractiveElement } from "../../../common/components/InteractiveElement";

  import type { Props } from "./IconButtonV3.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    variant = "secondary-over-color",
    elevationLevel = undefined,
    backgroundColor = undefined,
    disabled = false,
    icon,
    iconColor = undefined,
    size = "50",
    label = undefined,
    loading = false,
    success = false,
    ocAriaLabel,
    base64Href = undefined,
    rel = undefined,
    role = null,
    target = undefined,
  }: Props & {
    role?: string | null;
  } = $props();

  const Host = $host<HTMLElement>();

  const spinnerVariant: Record<Exclude<typeof variant, undefined>, OcSpinnerV1Props["variant"]> = {
    "secondary-over-color": "default",
    "secondary-on-color": "default",
    primary: "default",
    secondary: "default",
    tertiary: "default",
    transparent: "default",
    "transparent-inverted": "inverted",
  };

  const spinnerSize: Record<Exclude<typeof size, undefined>, OcSpinnerV1Props["size"]> = {
    "100": "100",
    "50": "100",
    "25": "50",
  };

  const iconSize: Record<Exclude<typeof size, undefined>, OcIconV1Props["size"]> = {
    "100": "100",
    "50": "100",
    "25": "50",
  };

  const handleClick = (event: MouseEvent | KeyboardEvent): void => {
    if (loading) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  Host.addEventListener("click", handleClick);
</script>

<!-- eslint-disable svelte/no-at-html-tags -->
<!-- eslint-disable no-nested-ternary -->
{#snippet interactiveElement()}
  <InteractiveElement
    {Host}
    asButton={role === null || role !== "none"}
    bind:base64Href
    class={`icon-button icon-button--size-${size} ${elevationLevel ? `icon-button--elevation-${elevationLevel}` : ""} icon-button--variant-${variant} ${variant === "secondary-on-color" ? "icon-button--variant-secondary-over-color" : ""} ${disabled ? "icon-button--disabled" : ""} ${loading ? " icon-button--is-loading" : ""} ${success ? "icon-button--success" : ""} ${backgroundColor ? "icon-button--has-custom-background-color" : ""}`}
    style={`${backgroundColor ? `--background-color: ${backgroundColor};` : ""}`}
    {ocAriaLabel}
    {disabled}
    aria-disabled={disabled || loading}
    {rel}
    {target}
  >
    {#snippet children(onslotchange)}
      {#if loading}
        <oc-spinner-v1
          in:fade={{ duration: 250 }}
          class="icon-button__icon"
          variant={spinnerVariant[variant]}
          size={spinnerSize[size ?? "50"]}
        ></oc-spinner-v1>
      {:else}
        <oc-icon-v1
          aria-hidden="true"
          class="icon-button__icon"
          class:icon-button__icon--disabled={disabled}
          size={iconSize[size ?? "50"]}
          type={icon}
          color={disabled ? undefined : iconColor}
        ></oc-icon-v1>
      {/if}
      <slot {onslotchange} />
    {/snippet}
  </InteractiveElement>
{/snippet}

{#if label}
  <oc-tooltip-v1 position="top">
    {@render interactiveElement()}
    <div aria-hidden="true" slot="tooltip-content">{label}</div>
  </oc-tooltip-v1>
{:else}
  {@render interactiveElement()}
{/if}

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "../../floating-focus/v1/FloatingFocusV1.shared" as floating-focus;

  :host {
    display: inline-block;
    outline: none;
    vertical-align: middle;

    /*        */
    --background-color: unset;
    --icon-color: unset;
  }

  :host([disabled]) {
    pointer-events: none;
  }

  /*                                                */
  .icon-button:before,
  ::slotted(*) {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    min-width: tokens.$oc-component-icon-button-50-hitbox-size;
    min-height: tokens.$oc-component-icon-button-50-hitbox-size;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    @include floating-focus.full-bleed();
  }

  /*                 */
  .icon-button {
    display: block;
    position: relative;
    border-radius: 50%;
    border: none;
    height: tokens.$oc-base-dimension-relative-32;
    width: tokens.$oc-base-dimension-relative-32;
    cursor: pointer;
    outline: none;
    outline-offset: 0;

    &--size-100 {
      width: tokens.$oc-component-icon-button-100-background-size;
      height: tokens.$oc-component-icon-button-100-background-size;
    }

    &--size-25 {
      width: tokens.$oc-component-icon-button-25-background-size;
      height: tokens.$oc-component-icon-button-25-background-size;

      &:before {
        min-width: tokens.$oc-component-icon-button-25-hitbox-size;
        min-height: tokens.$oc-component-icon-button-25-hitbox-size;
      }
    }

    &--elevation-canvas {
      background-color: var(--background-color, tokens.$oc-semantic-color-canvas-background);
      filter: tokens.$oc-semantic-shadow-canvas;
    }

    &--elevation-above {
      background-color: var(--background-color, tokens.$oc-semantic-color-above-background);
      filter: tokens.$oc-semantic-shadow-above;
    }

    &--elevation-sticky {
      background-color: var(--background-color, tokens.$oc-semantic-color-sticky-background);
      filter: tokens.$oc-semantic-shadow-sticky;
    }

    &--variant-secondary-over-color {
      background-color: var(
        --background-color,
        tokens.$oc-component-icon-button-secondary-over-color-background-color
      );

      &:not(.icon-button--is-loading):not(.icon-button--has-custom-background-color) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            background-color: tokens.$oc-component-icon-button-secondary-over-color-background-color-hover;
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          background-color: tokens.$oc-component-icon-button-secondary-over-color-background-color-active;
        }
      }

      .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
        --color: var(
          --icon-color,
          #{tokens.$oc-component-icon-button-secondary-over-color-icon-color}
        );
      }
    }

    &--variant-primary {
      background-color: var(
        --background-color,
        tokens.$oc-component-icon-button-primary-background-color
      );

      &:not(.icon-button--is-loading):not(.icon-button--has-custom-background-color) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            background-color: tokens.$oc-component-icon-button-primary-background-color-hover;
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          background-color: tokens.$oc-component-icon-button-primary-background-color-active;
        }
      }

      .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
        --color: var(--icon-color, #{tokens.$oc-component-icon-button-primary-icon-color});
      }
    }

    &--variant-secondary {
      background-color: var(
        --background-color,
        tokens.$oc-component-icon-button-secondary-background-color
      );

      &:not(.icon-button--is-loading):not(.icon-button--has-custom-background-color) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            background-color: tokens.$oc-component-icon-button-secondary-background-color-hover;
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          background-color: tokens.$oc-component-icon-button-secondary-background-color-active;
        }
      }

      .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
        --color: var(--icon-color, #{tokens.$oc-component-icon-button-secondary-icon-color});
      }
    }

    &--variant-tertiary {
      background-color: var(
        --background-color,
        tokens.$oc-component-icon-button-transparent-background-color
      );

      &:not(.icon-button--is-loading):not(.icon-button--has-custom-background-color) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            background-color: tokens.$oc-component-icon-button-transparent-background-color-hover;
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          background-color: tokens.$oc-component-icon-button-transparent-background-color-active;
        }
      }

      .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
        --color: var(--icon-color, #{tokens.$oc-component-icon-button-tertiary-icon-color});
      }
    }

    &--variant-transparent {
      background-color: var(
        --background-color,
        tokens.$oc-component-icon-button-transparent-background-color
      );

      &:not(.icon-button--is-loading):not(.icon-button--has-custom-background-color) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            background-color: tokens.$oc-component-icon-button-transparent-background-color-hover;
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          background-color: tokens.$oc-component-icon-button-transparent-background-color-active;
        }
      }

      .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
        --color: var(--icon-color, #{tokens.$oc-component-icon-button-transparent-icon-color});
      }
    }

    &--variant-transparent-inverted {
      background-color: var(
        --background-color,
        tokens.$oc-component-icon-button-inverted-background-color
      );

      &:not(.icon-button--is-loading):not(.icon-button--has-custom-background-color) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            background-color: tokens.$oc-component-icon-button-inverted-background-color-hover;
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          background-color: tokens.$oc-component-icon-button-inverted-background-color-active;
        }
      }

      .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
        --color: var(--icon-color, #{tokens.$oc-component-icon-button-inverted-icon-color});
      }
    }

    &__icon {
      --color: var(
        --icon-color,
        #{tokens.$oc-component-icon-button-secondary-over-color-icon-color}
      );
      left: 50%;
      top: 50%;
      position: absolute;
      transform: translate(-50%, -50%);

      &--disabled {
        --color: #{tokens.$oc-component-icon-button-disabled-icon-color};
      }
    }

    &:disabled,
    &.icon-button--disabled {
      background-color: tokens.$oc-component-icon-button-disabled-background-color;
    }

    &.icon-button--success,
    &.icon-button--success.icon-button--disabled,
    &.icon-button--success:disabled {
      background-color: var(
        --background-color,
        tokens.$oc-component-icon-button-success-background-color
      );

      &:not(.icon-button--is-loading):not(.icon-button--has-custom-background-color) {
        @media (hover: hover) {
          &:hover:not(:disabled):not(.icon-button--disabled) {
            background-color: tokens.$oc-component-icon-button-success-background-color-hover;
          }
        }

        &:active:not(:disabled):not(.icon-button--disabled) {
          background-color: tokens.$oc-component-icon-button-success-background-color-active;
        }
      }

      /*                                                           */
      .icon-button__icon {
        --color: #{tokens.$oc-component-icon-button-success-icon-color};
      }

      .icon-button__icon:not(.icon-button__icon--disabled):not(.icon-button--disabled) {
        --color: var(--icon-color, #{tokens.$oc-component-icon-button-success-icon-color});
      }
    }

    &--is-loading {
      cursor: progress;
    }

    &--has-custom-background-color {
      &:not(.icon-button--is-loading) {
        @supports (background-color: color-mix(in srgb, red, blue)) {
          @media (hover: hover) {
            &:hover:not(:disabled):not(.icon-button--disabled) {
              background-color: color-mix(in srgb, black 10%, var(--background-color));
            }
          }

          &:active:not(:disabled):not(.icon-button--disabled) {
            background-color: color-mix(in srgb, black 20%, var(--background-color));
          }
        }
        @supports not (background-color: color-mix(in srgb, red, blue)) {
          @media (hover: hover) {
            &:hover:not(:disabled):not(.icon-button--disabled) {
              filter: brightness(0.9);
            }

            &:active:not(:disabled):not(.icon-button--disabled) {
              filter: brightness(0.8);
            }
          }
        }
      }
    }
  }

  oc-tooltip-v1 {
    width: 100%;
    display: block;
    line-height: 0;
  }
</style>
